import React from 'react';
import { useRouter } from 'next/router';
import Styles from './Page.module.scss';
import { VendorLayoutV2 } from '~/components/common/layouts/VendorLayoutV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { ConstructionTop } from '~/components/common/constructions/ConstructionTop';
import { ConstructionList } from '~/components/common/constructions/list/ConstructionList';
import { BreadcrumbItem } from '~/components/common/parts/breadcrumb/Breadcrumbs';
import { PATH } from '~/pages/vendors/deals/[id]';
import { PATH as DEALS_PATH } from '~/pages/vendors/deals';
import { PATH as ESTIMATIONS_EDIT_PATH } from '~/pages/vendors/deals/[id]/estimations/edit';
import { useDeals } from '~/hooks/vendors/useDeals';
import Loading from '~/components/common/parts/loading/Loading';
import { useDealEstimations } from '~/hooks/vendors/deals/useDealEstimations';
import { getDownloadPath } from '~/utils/api/vendors/deals/DealEstimationFile';
import { getDownloadPath as getHistoryDownloadPath } from '~/utils/api/vendors/deals/DealEstimationHistoryFile';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { ConstructionConfirmedFooter } from '~/components/common/constructions/ConstructionConfirmedFooter';
import {
  getOriginDealVendor,
  isOriginVendor
} from '~/models/DealVendorCompany';
import { useSessions } from '~/hooks/vendors/useSessions';
import {
  update,
  updateAccept
} from '~/utils/api/vendors/deals/DealEstimationHeader';
import { NskResponse } from '~/models/Api';
import { useDealEstimationHeaders } from '~/hooks/vendors/deals/useDealEstimationHeaders';
import { isOwnStatus } from '~/models/Status';
import { estimationRequestSide } from '~/models/DealEstimationHeader';

export const Page: React.FC = () => {
  const id = useIdQueryHandler();
  const router = useRouter();
  const { data: deal } = useDeals().show(id);
  const { data: session } = useSessions().index();
  const { data: estimationContext, mutate: mutateEstimations } =
    useDealEstimations().index(id);
  const { data: dealEstimationHeaders, mutate: mutateHeaders } =
    useDealEstimationHeaders().index(id);
  const requestHeader =
    dealEstimationHeaders?.find((header) => header.status === 'request') ??
    null;
  const { data: estimationHistoryContext, isLoading } =
    useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
  React.useEffect(() => {
    if (deal === null) return;
    if (estimationRequestSide(deal, 'vendor')) {
      router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
    }
  }, [deal, router]);
  if (
    deal === null ||
    isLoading ||
    estimationContext === null ||
    session === null ||
    dealEstimationHeaders === null
  ) {
    return <Loading isLoading />;
  }
  const breadcrumbItems: BreadcrumbItem[] = [
    { path: DEALS_PATH, caption: '案件一覧', active: false },
    { path: PATH(deal.id), caption: '案件詳細', active: false },
    { path: '', caption: '見積り確認', active: true }
  ];
  const handleClickPreview = (): void => {
    const path = getDownloadPath(deal.id);
    window.open(path);
  };
  const handleClickHistoryPreview = (): void => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    const path = getHistoryDownloadPath(deal.id, requestHeader.id);
    window.open(path);
  };
  const handleMutateConstructions = async (): Promise<void> => {
    mutateEstimations && (await mutateEstimations());
    mutateHeaders && (await mutateHeaders());
  };
  const handleAcceptUpdateStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return updateAccept(deal.id, requestHeader.id);
  };
  const handleUpdateRejectStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return update(deal.id, requestHeader.id, 'reject');
  };
  return (
    <VendorLayoutV2>
      <DealLayout breadcrumbItems={breadcrumbItems} deal={deal} isVendor>
        <h2 className={Styles.EstimateTitle}>見積り確認</h2>
        <ConstructionTop
          constructionContext={estimationContext}
          dealEstimationHistoryContext={
            estimationHistoryContext ?? estimationHistoryContext
          }
          handleClickEstimationHistoryPreview={
            requestHeader ? handleClickHistoryPreview : null
          }
          handleClickEstimationPreview={handleClickPreview}
          readonly
        />
        <div className={Styles.EstimateList}>
          <ConstructionList
            companyType="vendor"
            constructionContext={estimationHistoryContext ?? estimationContext}
            constructionItems={[]}
            deal={deal}
            docType="estimation"
            handleMutateConstructions={handleMutateConstructions}
            readonly
            showEstimationConfirmedButton={
              (requestHeader &&
                !isOwnStatus(deal.status) &&
                getOriginDealVendor(deal)?.skipEstimation &&
                isOriginVendor(deal, session.vendorCompany.id)) ??
              false
            }
            showHighlight
          />
          {requestHeader && (
            <ConstructionConfirmedFooter
              companyType={
                isOriginVendor(deal, session.vendorCompany.id)
                  ? 'vendor'
                  : 'partner'
              }
              deal={deal}
              dealEstimationHeader={requestHeader}
              handleMutate={handleMutateConstructions}
              handleUpdateAcceptStatus={handleAcceptUpdateStatus}
              handleUpdateRejectStatus={handleUpdateRejectStatus}
              isAdmin={session.admin}
            />
          )}
        </div>
      </DealLayout>
    </VendorLayoutV2>
  );
};
